var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// x-input.ts
import { css } from "@adornis/chemistry/directives/css";
import "@adornis/chemistry/elements/components/x-flex";
import "@adornis/chemistry/elements/components/x-icon";
import { DesignSystem } from "@adornis/chemistry/elements/theming/design";
import { inputToken } from "@adornis/chemistry/elements/theming/input-token";
import { xComponents } from "@adornis/chemistry/elements/x-components";
import { html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ElementsPlainInput } from "./x-plain-input";
var ElementsInput = class extends ElementsPlainInput {
  constructor() {
    super(...arguments);
    this.placeholder = "";
    this["placeholder-mode"] = "floating";
    this.icon = "";
    this.clearable = false;
    this.postfix = "";
    this.bold = false;
    this["no-password-toggle"] = false;
    this.isPasswordInput = false;
  }
  get inputToken() {
    return inputToken(this);
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          height: "unset",
          fontSize: this.baseFontSize,
          "--placeholder-color": this.colors?.neutral_800,
          cursor: "text"
        },
        [`:host ${DesignSystem.prefix}-flex > ${DesignSystem.prefix}-flex`]: {
          ...this.inputToken,
          height: `calc(${this.baseFontSize}px + ${this.inputToken.paddingTop} + ${this.inputToken.paddingBottom} + 2px)`
        },
        [`:host([error]) ${DesignSystem.prefix}-flex > ${DesignSystem.prefix}-flex`]: {
          borderColor: this.colors?.tone.error
        },
        [`:host input[placeholder-mode="inline-floating"]`]: {
          transform: this._showFloatingLabel ? "translate(0px, 0.4em)" : "translate(0)",
          "&::placeholder": {
            color: this._showFloatingLabel ? "transparent" : void 0
          }
        }
      }
    ];
  }
  get _showLabel() {
    return this.value.value !== void 0 && this.value.value !== null && this.value.value !== "" || !!this.error;
  }
  get _showFloatingLabel() {
    return this.focused || this._showLabel;
  }
  render() {
    return xComponents(html`
      <x-flex ${css({ position: "relative" })}>
        ${this._label()}
        <x-flex horizontal crossaxis-center space="sm">
          ${this._prefixLabel()}
          <x-text flex ?bold=${this.bold}> ${super.render()} </x-text>
          ${this.postfix ? html` <x-text tone="subtle"> ${this.postfix} </x-text> ` : nothing} ${this.clearableIcon()}
          ${this.iconDisplay()}
        </x-flex>
      </x-flex>
    `);
  }
  clearableIcon() {
    return this.clearable && this._showLabel ? html`
          <x-icon
            ${css({ cursor: "pointer" })}
            @click=${() => {
      this.value.next("");
      this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: this.value.value } }));
      this.dispatchEvent(new CustomEvent("cleared"));
    }}
          >
            close
          </x-icon>
        ` : nothing;
  }
  iconDisplay() {
    return this.icon || (this.type === "password" || this.isPasswordInput) && !this["no-password-toggle"] ? html`
          <x-icon
            ${css({ marginRight: this.spacing?.sm, cursor: "pointer" })}
            @click="${() => {
      if (!this.icon && (this.type === "password" || this.isPasswordInput)) {
        if (this.type === "text" && this.isPasswordInput)
          this.type = "password";
        else if (this.type === "password") {
          this.isPasswordInput = true;
          this.type = "text";
        }
      }
      this.dispatchEvent(new CustomEvent("icon-click"));
    }}"
          >
            ${this.icon || (this.type === "password" ? "visibility_off" : this.isPasswordInput ? "visibility_on" : "")}
          </x-icon>
        ` : "";
  }
  _label() {
    if (!(this.placeholder || this.error))
      return nothing;
    if (this["placeholder-mode"] === "floating") {
      return html`
        <x-text
          ${css({
        marginLeft: this.spacing?.md,
        marginBottom: !!this._showLabel ? this.spacing?.xs : "0",
        opacity: !!this._showLabel ? "1" : "0",
        transform: !!this._showLabel ? "translateY(0)" : "translateY(20px)",
        transition: "opacity .12s ease, transform .12s ease, max-height .12s ease, margin .12s ease",
        maxHeight: !!this._showLabel ? "unset" : "0px",
        color: this.error ? this.colors?.tone.error : void 0,
        pointerEvents: "none"
      })}
          tone="subtle"
        >
          ${this.error || this.placeholder}
        </x-text>
      `;
    } else if (this["placeholder-mode"] === "inline-floating") {
      return html`
        <x-text
          ${css({
        position: "absolute",
        marginLeft: this.spacing?.md,
        transformOrigin: "left top",
        top: "50%",
        transform: !!this._showFloatingLabel ? "translateY(-1em) scale(0.7, 0.7)" : "translateY(0)",
        opacity: !!this._showFloatingLabel ? "1" : "0",
        transition: "transform .12s ease, max-height .12s ease",
        color: this.error ? this.colors?.tone.error : void 0,
        pointerEvents: "none",
        maxHeight: !!this._showFloatingLabel ? "unset" : "0",
        maxWidth: "100%",
        minWidth: "0",
        whiteSpace: "nowrap",
        overflow: "hidden",
        height: "2rem",
        textOverflow: "ellipsis"
      })}
          tone="subtle"
        >
          ${this.error || this.placeholder}
        </x-text>
      `;
    }
    return nothing;
  }
  _prefixLabel() {
    return this["placeholder-mode"] === "prefix" && this.placeholder && this._showLabel ? html` <x-text flex-none tone="subtle"> ${this.placeholder} </x-text> ` : nothing;
  }
};
__decorateClass([
  property({ reflect: true, attribute: true })
], ElementsInput.prototype, "placeholder", 2);
__decorateClass([
  property({ reflect: true, attribute: true })
], ElementsInput.prototype, "placeholder-mode", 2);
__decorateClass([
  property({ attribute: true })
], ElementsInput.prototype, "icon", 2);
__decorateClass([
  property({ type: Boolean, attribute: true })
], ElementsInput.prototype, "clearable", 2);
__decorateClass([
  property({ attribute: true })
], ElementsInput.prototype, "postfix", 2);
__decorateClass([
  property({ type: Boolean, attribute: true })
], ElementsInput.prototype, "bold", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], ElementsInput.prototype, "no-password-toggle", 2);
__decorateClass([
  state()
], ElementsInput.prototype, "isPasswordInput", 2);
ElementsInput = __decorateClass([
  customElement("x-input")
], ElementsInput);
export {
  ElementsInput
};
